import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Grid, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from '../../components/toast';
import { useLoadingState } from '../../hooks/useLoadingState';
import DeleteConfirmationDialog from '../../components/common/DeleteConfirmationDialog';
import {
  InventoryAssignmentStatsCards,
  InventoryAssignmentFilters,
  InventoryAssignmentTable,
  InventoryAssignmentForm,
  ReturnInventoryDialog,
} from '../../components/inventoryAssignment';
import { inventoryAssignmentValidationSchema } from '../../utils/inventoryAssignmentValidation';
import {
  getInitialInventoryAssignmentFormData,
  getInventoryAssignmentFormDataFromRecord,
  formatInventoryAssignmentDataForSubmission,
} from '../../utils/inventoryAssignmentUtils';
import { useInventoryAssignmentOperations } from '../../hooks/useInventoryAssignmentOperations';
import { getUserGroups } from '../../utils';
import { HRPageHeader } from '../../components/hr';
import { LightTooltip } from '../../components/custom-components/custom-tooltip';

const allowedGroups = ['admin', 'hr'];

export default function InventoryAssignmentManagementPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [dialogMode, setDialogMode] = useState('add');
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [inventoryItemFilter, setInventoryItemFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  const [assignmentToReturn, setAssignmentToReturn] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState(getInitialInventoryAssignmentFormData());
  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });

  const {
    inventoryAssignments,
    stats,
    totalCount,
    filterOptions,
    loadInventoryAssignments,
    loadFilterOptions,
    loadStats,
    createInventoryAssignmentRecord,
    updateInventoryAssignmentRecord,
    deleteInventoryAssignmentRecord,
    returnInventoryAssignmentRecord,
    getInventoryAssignmentDetailsRecord,
  } = useInventoryAssignmentOperations();

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || !allowedGroups.some((group) => userGroups.includes(group))) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const loadData = useCallback(async () => {
    await withLoading(async () => {
      await Promise.all([
        loadInventoryAssignments(page, rowsPerPage, searchTerm, employeeFilter, inventoryItemFilter, statusFilter),
        loadFilterOptions(),
        loadStats(),
      ]);
    });
  }, [
    page,
    rowsPerPage,
    searchTerm,
    employeeFilter,
    inventoryItemFilter,
    statusFilter,
    withLoading,
    loadInventoryAssignments,
    loadFilterOptions,
    loadStats,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = async (mode, assignment = null) => {
    setDialogMode(mode);
    setSelectedAssignment(assignment);
    if ((mode === 'edit' || mode === 'view') && assignment) {
      try {
        const details = await getInventoryAssignmentDetailsRecord(assignment.id);
        if (details) {
          setFormData(getInventoryAssignmentFormDataFromRecord(details.assignment || details));
        } else {
          setFormData(getInventoryAssignmentFormDataFromRecord(assignment));
        }
      } catch (_) {
        setFormData(getInventoryAssignmentFormDataFromRecord(assignment));
      }
    } else {
      setFormData(getInitialInventoryAssignmentFormData());
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAssignment(null);
    setFormErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = async () => {
    try {
      await inventoryAssignmentValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      setFormErrors(errors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    try {
      const payload = formatInventoryAssignmentDataForSubmission(formData);
      let success = false;

      if (dialogMode === 'add') {
        success = await createInventoryAssignmentRecord(payload);
      } else {
        success = await updateInventoryAssignmentRecord(selectedAssignment.id, payload);
      }

      if (success) {
        handleCloseDialog();
        loadData();
      }
    } catch (err) {
      console.error('Submit error:', err);
    }
  };

  const handleDeleteAssignment = (assignment) => {
    setAssignmentToDelete(assignment);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (assignmentToDelete) {
      const success = await deleteInventoryAssignmentRecord(assignmentToDelete.id);
      if (success) {
        setOpenDeleteDialog(false);
        setAssignmentToDelete(null);
        loadData();
      }
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setAssignmentToDelete(null);
  };

  const handleReturnAssignment = async (assignment) => {
    setAssignmentToReturn(assignment);
    setOpenReturnDialog(true);
  };

  const handleReturnSubmit = async (returnData) => {
    if (assignmentToReturn) {
      const success = await returnInventoryAssignmentRecord(assignmentToReturn.id, returnData);
      if (success) {
        setOpenReturnDialog(false);
        setAssignmentToReturn(null);
        loadData();
      }
    }
  };

  const handleReturnCancel = () => {
    setOpenReturnDialog(false);
    setAssignmentToReturn(null);
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Inventory Assignment Management</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HRPageHeader
            title="Inventory assignments"
            description="Assign catalog items to employees, track who holds each asset, and process returns when equipment comes back."
            actions={
              <LightTooltip title="Assign an inventory item to an employee.">
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('add')}>
                  New assignment
                </Button>
              </LightTooltip>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <InventoryAssignmentStatsCards stats={stats} isLoading={isLoading} />
        </Grid>

        <InventoryAssignmentFilters
          searchTerm={searchTerm}
          employeeFilter={employeeFilter}
          inventoryItemFilter={inventoryItemFilter}
          statusFilter={statusFilter}
          filterOptions={filterOptions}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setPage(0);
          }}
          onEmployeeFilterChange={(value) => {
            setEmployeeFilter(value);
            setPage(0);
          }}
          onInventoryItemFilterChange={(value) => {
            setInventoryItemFilter(value);
            setPage(0);
          }}
          onStatusFilterChange={(value) => {
            setStatusFilter(value);
            setPage(0);
          }}
          onClearFilters={() => {
            setSearchTerm('');
            setEmployeeFilter('all');
            setInventoryItemFilter('all');
            setStatusFilter('all');
            setPage(0);
          }}
        />

        <Grid item xs={12}>
          <InventoryAssignmentTable
            inventoryAssignments={inventoryAssignments}
            isLoading={isLoading}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onViewAssignment={(assignment) => handleOpenDialog('view', assignment)}
            onEditAssignment={(assignment) => handleOpenDialog('edit', assignment)}
            onDeleteAssignment={handleDeleteAssignment}
            onReturnAssignment={handleReturnAssignment}
          />
        </Grid>
      </Grid>

      <InventoryAssignmentForm
        open={openDialog}
        mode={dialogMode}
        formData={formData}
        formErrors={formErrors}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        title="Confirm Delete"
        message="Are you sure you want to delete this inventory assignment"
        itemName={assignmentToDelete?.inventory_item?.name || 'assignment'}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmButtonText="Delete Assignment"
      />

      <ReturnInventoryDialog
        open={openReturnDialog}
        onClose={handleReturnCancel}
        onSubmit={handleReturnSubmit}
        assignment={assignmentToReturn}
        isLoading={isLoading}
      />
    </>
  );
}
